import { useMemo, useState } from "react";
import { Reveal } from "../lib/reveal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Wind, FlaskConical, Timer, IndianRupee, Download, Mail, Flame } from "lucide-react";
import { APPLICATIONS, computeRoi, inr } from "../lib/roi";
import { generateRoiPdf } from "../lib/roiPdf";
import { useLang } from "../i18n/LanguageContext";

export const ROICalculator = () => {
  const { t } = useLang();
  const [application, setApplication] = useState("Legacy Landfill / SWM");
  const [tonnes, setTonnes] = useState(80);
  const [spend, setSpend] = useState(150000);

  const r = useMemo(
    () => computeRoi({ application, tonnesPerDay: tonnes, monthlySpend: spend }),
    [application, tonnes, spend]
  );

  const emailEstimate = () => {
    const subject = encodeURIComponent("OED ROI estimate — Fresh Earth");
    const body = encodeURIComponent(
      `OED ROI Snapshot\n\n` +
        `Application: ${r.application}\n` +
        `Throughput: ${r.tonnesPerDay} tonnes/day\n` +
        `Dosage: ${r.dosage} L/tonne\n\n` +
        `Odour control: ${r.odour}% (within a week)\n` +
        `Weight reduction: ${r.reduction || "—"}%\n` +
        (r.biogas ? `Biogas increase: +${r.biogas}%\n` : `Decomposition: ${r.speed} faster\n`) +
        `\nPer-tonne: cost ₹${inr(r.costPerTonne)} · recovered ₹${inr(r.revenuePerTonne)} · net ₹${inr(r.netPerTonne)}\n` +
        `Estimated annual value/savings: ₹${inr(r.annualNetValue)}\n\n` +
        `Please contact me to discuss a pilot.\n`
    );
    window.location.href = `mailto:care@fresh-earth.com?subject=${subject}&body=${body}`;
  };

  const third = r.biogas
    ? { icon: Flame, label: t("Biogas increase", "बायोगैस वृद्धि"), value: `+${r.biogas}%` }
    : { icon: Timer, label: t("Decomposition", "अपघटन"), value: r.speed };

  return (
    <section id="roi" data-testid="roi-section" className="py-24 md:py-32 bg-forest text-bone grain relative overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <Reveal className="flex items-center gap-3 mb-6">
            <span className="text-xs tracking-[0.22em] uppercase font-semibold text-terracotta">{t("ROI Estimator", "ROI अनुमानक")}</span>
          </Reveal>
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              {t("See the impact before you commit.", "प्रतिबद्ध होने से पहले प्रभाव देखें।")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-bone/70">
              {t(
                "Estimate the odour, decomposition and cost impact of an OED programme on your site. Figures are indicative, grounded in Fresh Earth field deployments.",
                "अपनी साइट पर OED कार्यक्रम के गंध, अपघटन व लागत प्रभाव का अनुमान लगाएं। आंकड़े सांकेतिक हैं, जो Fresh Earth की फ़ील्ड तैनातियों पर आधारित हैं।"
              )}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Inputs */}
          <Reveal className="lg:col-span-5">
            <div className="bg-bone/[0.06] backdrop-blur-sm rounded-3xl border border-bone/15 p-8 md:p-10 space-y-9">
              <div>
                <label className="text-xs tracking-[0.14em] uppercase text-bone/60">{t("Application", "अनुप्रयोग")}</label>
                <Select value={application} onValueChange={setApplication}>
                  <SelectTrigger data-testid="roi-application" className="mt-2 w-full bg-transparent rounded-none border-0 border-b border-bone/30 px-0 py-2 h-auto text-lg text-bone focus:ring-0 focus:border-terracotta [&>span]:text-bone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-forest text-bone border-bone/20">
                    {Object.keys(APPLICATIONS).map((s) => (
                      <SelectItem key={s} value={s} data-testid={`roi-app-${s}`} className="focus:bg-bone/10 focus:text-bone">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs tracking-[0.14em] uppercase text-bone/60">{t("Waste throughput", "अपशिष्ट प्रवाह")}</label>
                  <span className="font-serif text-xl text-terracotta">{tonnes} t/day</span>
                </div>
                <input
                  data-testid="roi-tonnes"
                  type="range" min="5" max="700" step="5"
                  value={tonnes}
                  onChange={(e) => setTonnes(Number(e.target.value))}
                  style={{ accentColor: "#B86551" }}
                  className="mt-4 w-full cursor-pointer"
                />
              </div>

              <div>
                <label className="text-xs tracking-[0.14em] uppercase text-bone/60">{t("Current monthly odour-mitigation spend (₹)", "वर्तमान मासिक गंध-निवारण व्यय (₹)")}</label>
                <input
                  data-testid="roi-spend"
                  type="number" min="0" step="10000"
                  value={spend}
                  onChange={(e) => setSpend(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-transparent rounded-none border-0 border-b border-bone/30 py-2 mt-2 text-lg text-bone placeholder:text-bone/50 focus:outline-none focus:border-terracotta transition-colors duration-300"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button onClick={() => generateRoiPdf(r)} data-testid="roi-download-pdf" className="inline-flex items-center gap-2 rounded-full bg-bone text-forest text-sm tracking-[0.08em] uppercase px-6 py-3 hover:bg-terracotta hover:text-bone transition-colors duration-300">
                  <Download size={16} /> {t("Download PDF", "PDF डाउनलोड करें")}
                </button>
                <button onClick={emailEstimate} data-testid="roi-email" className="inline-flex items-center gap-2 rounded-full border border-bone/30 text-bone text-sm tracking-[0.08em] uppercase px-6 py-3 hover:bg-bone/10 transition-colors duration-300">
                  <Mail size={16} /> {t("Email estimate", "अनुमान ईमेल करें")}
                </button>
              </div>
            </div>
          </Reveal>

          {/* Results */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="roi-results">
            {[
              { icon: Wind, label: t("Odour control", "गंध नियंत्रण"), value: `${r.odour}%`, sub: t("within a week", "एक सप्ताह में") },
              { icon: FlaskConical, label: t("Weight reduction", "वज़न में कमी"), value: r.reduction ? `${r.reduction}%` : "—", sub: t("of treated waste", "उपचारित अपशिष्ट का") },
              { icon: third.icon, label: third.label, value: third.value, sub: r.biogas ? t("observed", "देखा गया") : t("faster vs natural", "प्राकृतिक की तुलना में तेज़") },
            ].map((m) => (
              <div key={m.label} className="bg-bone/[0.06] rounded-3xl border border-bone/15 p-8 flex flex-col">
                <m.icon size={22} strokeWidth={1.4} className="text-terracotta" />
                <div className="font-serif text-5xl md:text-6xl mt-6 leading-none">{m.value}</div>
                <div className="text-xs tracking-[0.14em] uppercase text-bone/60 mt-3">{m.label}</div>
                <div className="text-xs text-bone/40 mt-1">{m.sub}</div>
              </div>
            ))}
            <div className="bg-terracotta text-bone rounded-3xl p-8 flex flex-col justify-between">
              <IndianRupee size={22} strokeWidth={1.6} />
              <div>
                <div className="font-serif text-4xl md:text-5xl mt-6 leading-none" data-testid="roi-savings">₹{inr(r.annualNetValue)}</div>
                <div className="text-xs tracking-[0.14em] uppercase text-bone/80 mt-3">{t("Est. annual value / savings", "अनुमानित वार्षिक मूल्य / बचत")}</div>
                <div className="text-xs text-bone/70 mt-2">Net ₹{inr(r.netPerTonne)}/tonne · treat ₹{inr(r.costPerTonne)} · recover ₹{inr(r.revenuePerTonne)}</div>
              </div>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <a href="#contact" data-testid="roi-cta" className="inline-flex mt-10 items-center rounded-full bg-bone text-forest text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300">
            {t("Request a tailored assessment", "अनुकूलित मूल्यांकन का अनुरोध करें")}
          </a>
        </Reveal>
      </div>
    </section>
  );
};
